<script setup lang="ts">
import type { PropType } from 'vue'

interface CTimePickerProps {
  rounded?: string | number | boolean
  variant?:
  | 'filled'
  | 'outlined'
  | 'plain'
  | 'underlined'
  | 'solo'
  | 'solo-inverted'
  | 'solo-filled'
  format?: '24hr' | 'ampm'
  min?: string
  max?: string
  allowedHours?: number[] | ((hour: number) => boolean)
  allowedMinutes?: number[] | ((minute: number) => boolean)
  useSeconds?: boolean
  scrollable?: boolean
  ampmInTitle?: boolean
}

const props = defineProps({
  color: {
    type: String,
    default: '',
  },
  rounded: {
    type: String as PropType<CTimePickerProps['rounded']>,
    default: 'xl',
  },
  variant: {
    type: String as PropType<CTimePickerProps['variant']>,
    default: 'outlined',
    validator: (value: string): boolean =>
      [
        'filled',
        'outlined',
        'plain',
        'underlined',
        'solo',
        'solo-inverted',
        'solo-filled',
      ].includes(value),
  },
  label: {
    type: String,
    default: 'Seleccionar hora',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  hiddenDetail: {
    type: Boolean,
    default: true,
  },
  singleLine: {
    type: Boolean,
    default: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  format: {
    type: String as PropType<CTimePickerProps['format']>,
    default: '24hr',
    validator: (value: string): boolean => ['24hr', 'ampm'].includes(value),
  },
  min: {
    type: String as PropType<CTimePickerProps['min']>,
    default: undefined,
  },
  max: {
    type: String as PropType<CTimePickerProps['max']>,
    default: undefined,
  },
  allowedHours: {
    type: [Array, Function] as PropType<CTimePickerProps['allowedHours']>,
    default: undefined,
  },
  allowedMinutes: {
    type: [Array, Function] as PropType<CTimePickerProps['allowedMinutes']>,
    default: undefined,
  },
  useSeconds: {
    type: Boolean as PropType<CTimePickerProps['useSeconds']>,
    default: false,
  },
  scrollable: {
    type: Boolean as PropType<CTimePickerProps['scrollable']>,
    default: false,
  },
  ampmInTitle: {
    type: Boolean as PropType<CTimePickerProps['ampmInTitle']>,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  appendIcon: {
    type: String,
    default: 'mdi-clock-outline',
  },
})

const emits = defineEmits({
  changed: (event: unknown) => true,
})

const model = defineModel()
</script>

<template>
  <v-text-field :label="props.label" :variant="props.variant" :hide-details="props.hiddenDetail"
    :single-line="props.singleLine" :rounded="props.rounded" :loading="props.loading" :disabled="props.disabled"
    :readonly="props.readonly" :color="props.color" :clearable="props.clearable" :append-inner-icon="props.appendIcon"
    density="comfortable" v-model="model" @update:model-value="emits('changed', $event)">
    <v-menu activator="parent" :close-on-content-click="false">
      <v-time-picker :color="props.color" :format="props.format" :min="props.min" :max="props.max"
        :allowed-hours="props.allowedHours" :allowed-minutes="props.allowedMinutes" :use-seconds="props.useSeconds"
        :scrollable="props.scrollable" :ampm-in-title="props.ampmInTitle" v-model="model"
        @update:model-value="emits('changed', $event)"></v-time-picker>
    </v-menu>
  </v-text-field>
</template>
